import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../config/URL";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";

const SalesRevenueReport = ({ selectedCenter }) => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    centerId: selectedCenter || "",
    courseId: "",
    studentName: "",
    paymentType: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courseData, setCourseData] = useState(null);

  /* -------------------------------- API CALL -------------------------------- */

  const fetchSalesRevenueReport = async (filterValues = filters) => {
    if (!selectedCenter) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("centerId", selectedCenter);

      if (filterValues.courseId)
        params.append("courseId", filterValues.courseId);

      if (filterValues.studentName)
        params.append("studentName", filterValues.studentName);

      if (filterValues.paymentType)
        params.append("paymentType", filterValues.paymentType);

      if (filterValues.status) params.append("status", filterValues.status);

      if (filterValues.startDate)
        params.append("startDate", filterValues.startDate);

      if (filterValues.endDate) params.append("endDate", filterValues.endDate);

      const response = await api.get(`saleRevenueReport?${params.toString()}`);

      setTableData(response.data?.records || []);
      setSummary(response.data?.summary || null);
    } catch (error) {
      toast.error("Failed to fetch sales revenue report");
      setTableData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchListWithCenterIdData = async (centerId) => {
    try {
      const [courseDatas] = await Promise.all([
        fetchAllCoursesWithIdsC(centerId),
      ]);
      setCourseData(courseDatas || []);
    } catch (error) {
      toast.error("Failed to fetch center-specific data");
      console.error(error);
    }
  };

  /* -------------------------------- SYNC CENTER -------------------------------- */

  useEffect(() => {
    if (selectedCenter) {
      setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
      fetchSalesRevenueReport({
        ...filters,
        centerId: selectedCenter,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenter]);

  useEffect(() => {
    if (filters.centerId) {
      fetchListWithCenterIdData(filters.centerId);
    }
  }, [filters.centerId]);

  /* -------------------------------- COLUMNS -------------------------------- */

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        id: "sno",
        header: "S.NO",
        size: 40,
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const value = cell.getValue();

          if (!value) return "-";

          const statusMap = {
            PAID: { label: "Paid", className: "badge bg-success" },
            PENDING: {
              label: "Pending",
              className: "badge bg-warning text-dark",
            },
            CANCELLED: { label: "Cancelled", className: "badge bg-danger" },
            PARTIALLY_PAID: {
              label: "Partially Paid",
              className: "badge bg-info text-dark",
            },
          };

          const status = statusMap[value] || {
            label: value,
            className: "badge bg-secondary",
          };

          return <span className={status.className}>{status.label}</span>;
        },
      },

      {
        accessorKey: "studentName",
        header: "Student Name",
      },
      {
        accessorKey: "parentName",
        header: "Parent Name",
      },
      {
        accessorKey: "invoiceNo",
        header: "Invoice No",
        Cell: ({ row }) => (
          <Link
            to={`/invoice/view/${row.original.invoiceId}`}
            className="text-primary"
            style={{ textDecoration: "none", fontWeight: 500 }}
          >
            {row.original.invoiceNo}
          </Link>
        ),
      },
      {
        accessorKey: "receiptNo",
        header: "Receipt No",
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "invoiceDate",
        header: "Invoice Date",
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString("en-GB")
            : "--",
      },
      {
        accessorKey: "courseFee",
        header: "Course Fee",
        Cell: ({ cell }) => cell.getValue()?.toLocaleString() || "0",
      },
      {
        accessorKey: "materialFee",
        header: "Material Fee",
        Cell: ({ cell }) => cell.getValue()?.toLocaleString() || "0",
      },
      {
        accessorKey: "registerFee",
        header: "Register Fee",
        Cell: ({ cell }) => cell.getValue()?.toLocaleString() || "0",
      },
      {
        accessorKey: "depositFee",
        header: "Deposit Fee",
        Cell: ({ cell }) => cell.getValue()?.toLocaleString() || "0",
      },

      /* ================= TOTALS ================= */

      {
        accessorKey: "invoiceTotal",
        header: "Invoice Total",
        Cell: ({ cell }) =>
          Number(cell.getValue() || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }),
      },
      {
        accessorKey: "paidAmount",
        header: "Amount",
        Cell: ({ cell }) =>
          Number(cell.getValue() || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          }),
      },
      // {
      //   accessorKey: "totalAmount",
      //   header: "Amount",
      //   Cell: ({ cell }) =>
      //     Number(cell.getValue()).toLocaleString(undefined, {
      //       minimumFractionDigits: 2,
      //     }),
      // },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
    // muiTableBodyRowProps: ({ row }) => ({
    //   onClick: () => navigate(`/student/view/${row.original.studentId}`),
    //   style: { cursor: "pointer" },
    // }),
  });

  /* -------------------------------- THEME -------------------------------- */

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  /* -------------------------------- FILTER HANDLERS -------------------------------- */

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchSalesRevenueReport(filters);
  };

  const clearFilters = () => {
    const reset = {
      centerId: selectedCenter || "",
      courseId: "",
      studentName: "",
      paymentType: "",
      status: "",
      startDate: "",
      endDate: "",
    };
    setFilters(reset);
    fetchSalesRevenueReport(reset);
  };

  /* -------------------------------- UI -------------------------------- */

  return (
    <div className="container-fluid my-4 center">
      <ol className="breadcrumb my-3 px-2">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>{" "}
          &gt;
        </li>
        <li className="breadcrumb-item active">Sales Revenue Report</li>
      </ol>

      <div className="card">
        <div
          className="p-1 mb-3 d-flex align-items-center"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted ms-2">
            This database shows{" "}
            <strong style={{ color: "#287f71" }}>Sales Revenue Report</strong>
          </span>
        </div>

        {/* SUMMARY */}
        {summary && (
          <div className="px-3 mb-3">
            <h6>Revenue Summary</h6>
            <div className="d-flex flex-wrap gap-3">
              <span>
                Total Revenue:{" "}
                <strong>{summary.totalRevenue?.toLocaleString()}</strong>
              </span>
              <span>
                Paid Amount:{" "}
                <strong>{summary.paidAmount?.toLocaleString()}</strong>
              </span>
              <span>
                Outstanding:{" "}
                <strong>{summary.outstandingAmount?.toLocaleString()}</strong>
              </span>
            </div>
          </div>
        )}

        {/* FILTERS */}
        <div className="mb-3 px-2">
          <div className="individual_fliters d-lg-flex">
            {/* <select
              className="form-select form-select-sm center_list"
              name="courseId"
              onChange={handleFilterChange}
              value={filters.courseId}
            >
              <option value="">Select Courses</option>
              {courseData?.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.courseNames}
                </option>
              ))}
            </select> */}
            <input
              className="form-control form-control-sm center_list ms-2"
              placeholder="Student Name"
              name="studentName"
              value={filters.studentName}
              onChange={handleFilterChange}
            />

            <input
              type="date"
              className="form-control form-control-sm center_list ms-2"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />

            <input
              type="date"
              className="form-control form-control-sm center_list ms-2"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />

            <select
              className="form-select form-select-sm center_list ms-2"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="" disabled>
                Select Status
              </option>
              <option value="PAID">Paid</option>
              <option value="PENDING">Pending</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="PARTIALLY_PAID">Partially Paid</option>
            </select>
          </div>
          <div className="mt-2">
            <button
              className="btn btn-sm text-white ms-2"
              style={{ background: "#eb862a" }}
              onClick={applyFilters}
            >
              Search
            </button>

            <button
              className="btn btn-sm btn-border ms-2"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
          </ThemeProvider>
        )}
      </div>
    </div>
  );
};

export default SalesRevenueReport;
